import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, Rocket } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Sprint 1.0c — "Premiers pas" home checklist.
 *
 * Replaces the U4/U5 5-slide tutorial that was removed. Renders ONLY when the
 * backend says `visible=true`. Brief B5 P0 Bug 2 — `visible` is now true while
 * `done_count < 5 AND !dismissed` (no more 70% auto-dismiss).
 */
const HomeChecklist = ({ onDismissed }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/home/checklist`, { withCredentials: true })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({ visible: false }); });
    return () => { cancelled = true; };
  }, []);

  if (!data || !data.visible) return null;

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await axios.post(`${API}/home/checklist/dismiss`, {}, { withCredentials: true });
    } catch (e) {
      console.error('[checklist] dismiss failed:', e);
    } finally {
      setDismissing(false);
      setData({ ...data, visible: false });
      if (onDismissed) onDismissed();
    }
  };

  const pct = Math.max(0, Math.min(100, data.pct || 0));

  return (
    <section
      className="bg-white dark:bg-gray-800/60 border border-gray-200/70 dark:border-gray-700/60 rounded-xl px-5 py-4"
      style={{ borderWidth: '0.5px' }}
      data-testid="home-checklist"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2.5 min-w-0">
          <div className="h-8 w-8 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center shrink-0">
            <Rocket className="h-4 w-4 text-purple-600 dark:text-purple-300" />
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-white">Premiers pas</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">
              Complète ton profil pour être visible dans les recherches.
            </p>
          </div>
        </div>
        <span className="text-[11px] font-bold text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/40 px-2 py-0.5 rounded-full shrink-0" data-testid="home-checklist-pct">
          {pct}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 dark:bg-gray-700/60 rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
          style={{ width: `${pct}%` }}
          data-testid="home-checklist-progress"
        />
      </div>

      <ul className="space-y-1.5">
        {(data.items || []).map((it) => (
          <li key={it.key}>
            <button
              type="button"
              disabled={it.done}
              onClick={() => it.href && navigate(it.href)}
              data-testid={`home-checklist-item-${it.key}`}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left text-[12px] transition-colors ${
                it.done
                  ? 'text-gray-400 dark:text-gray-500 cursor-default'
                  : 'text-gray-700 dark:text-gray-200 hover:bg-purple-50 dark:hover:bg-purple-900/20'
              }`}
            >
              {it.done ? (
                <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0" />
              )}
              <span className={`flex-1 ${it.done ? 'line-through' : ''}`}>{it.label}</span>
              {!it.done && <ArrowRight className="h-3 w-3 text-gray-400 shrink-0" />}
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={handleDismiss}
          disabled={dismissing}
          className="text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 inline-flex items-center gap-0.5 disabled:opacity-60"
          data-testid="home-checklist-dismiss"
        >
          Passer <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </section>
  );
};

export default HomeChecklist;
